import type { JsonType } from 'posthog-js';

export const CACHE_VERSION = 2;

const KEY_PREFIX = 'eluve:feature-flags';

export type FeatureFlags = Record<string, string | boolean>;
export type FeatureFlagPayloads = Record<string, JsonType>;
export interface FeatureFlagsWithPayloads {
  flags: FeatureFlags;
  payloads: FeatureFlagPayloads;
}

// `CACHE_VERSION` is part of the localStorage key, not stored inside the value.
// Bumping it changes the key prefix so old entries simply become unreachable
// to read() — no in-value version check is needed for correctness.
interface CacheEntry {
  fetchedAt: number;
  flags: FeatureFlags;
  payloads?: FeatureFlagPayloads;
}

const keyFor = (userId: string, tenantId: string) =>
  `${KEY_PREFIX}:v${CACHE_VERSION}:${userId}:${tenantId}`;

const getStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const read = (
  userId: string,
  tenantId: string,
): FeatureFlagsWithPayloads | null => {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(keyFor(userId, tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CacheEntry>;
    if (typeof parsed?.flags !== 'object' || parsed.flags === null) {
      return null;
    }
    return {
      flags: parsed.flags,
      payloads:
        typeof parsed.payloads === 'object' && parsed.payloads !== null
          ? parsed.payloads
          : {},
    };
  } catch {
    return null;
  }
};

export const write = (
  userId: string,
  tenantId: string,
  value: FeatureFlagsWithPayloads,
): void => {
  const storage = getStorage();
  if (!storage) return;
  try {
    const entry: CacheEntry = {
      fetchedAt: Date.now(),
      flags: value.flags,
      payloads: value.payloads,
    };
    storage.setItem(keyFor(userId, tenantId), JSON.stringify(entry));
  } catch {
    // Quota exceeded, Safari private mode, etc. — caching is best-effort.
  }
};

export const clear = (userId: string, tenantId: string): void => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(keyFor(userId, tenantId));
  } catch {
    // ignore
  }
};
